import type { CameraPose } from '../data/types';
import type { Vec2, Vec3 } from '../geo/types';
import {
  derivePainterProjectionParams,
  STYLE_CONSTANTS,
  warpPosition,
} from '../render/painterProjection';
import {
  painterFrameFromView,
  painterFrameShift,
  projectToPainterFrame,
  type PainterResidualWarp,
} from '../render/painterWarp';
import { committedResidualWarp } from '../render/painterWarpData';

export interface PaintingMinimapMarker {
  /** Calibrated image pixels, x right and y down; the camera's altitude is ignored. */
  readonly pixel: Vec2;
  /** Unit horizontal look direction in the image's pixel axes. */
  readonly direction: Vec2 | null;
  readonly inside: boolean;
}

export interface PaintingMinimapModel {
  readonly imageSize: Vec2;
  projectGround(worldPosition: Vec3): Vec2 | null;
  marker(worldPosition: Vec3, yaw: number): PaintingMinimapMarker | null;
}

/** The same calibrated ground projection the painter renderer uses, regardless of the live lens,
 * altitude, pitch or painter amount. Marker coordinates refer to the painting's plan, not rooftops. */
export function createPaintingMinimapModel(
  painterView: CameraPose,
  residualWarp: PainterResidualWarp = committedResidualWarp()
): PaintingMinimapModel {
  const imageSize: Vec2 = [STYLE_CONSTANTS.imageWidth, STYLE_CONSTANTS.imageHeight];
  const params = derivePainterProjectionParams(painterView, STYLE_CONSTANTS);
  const frame = painterFrameFromView(painterView, STYLE_CONSTANTS);

  function projectGround(position: Vec3): Vec2 | null {
    if (!Number.isFinite(position[0]) || !Number.isFinite(position[2])) return null;
    const ground = warpPosition([position[0], 0, position[2]], params, 1);
    const projected = projectToPainterFrame(frame, ground);
    if (!(projected.depth > 0)) return null;
    const shift = painterFrameShift(frame, residualWarp, ground, 1);
    const pixel: Vec2 = [projected.pixel[0] + shift[0], projected.pixel[1] + shift[1]];
    return pixel.every(Number.isFinite) ? pixel : null;
  }

  return {
    imageSize,
    projectGround,
    marker(position, yaw) {
      const pixel = projectGround(position);
      if (!pixel) return null;
      // Controller yaw is a three.js rotation: north at zero, east at -pi/2.
      const ahead = Number.isFinite(yaw)
        ? projectGround([position[0] - Math.sin(yaw), 0, position[2] - Math.cos(yaw)])
        : null;
      const delta: Vec2 = ahead ? [ahead[0] - pixel[0], ahead[1] - pixel[1]] : [0, 0];
      const length = Math.hypot(delta[0], delta[1]);
      return {
        pixel,
        direction: length > 0 ? [delta[0] / length, delta[1] / length] : null,
        inside:
          pixel[0] >= 0 && pixel[0] <= imageSize[0] && pixel[1] >= 0 && pixel[1] <= imageSize[1],
      };
    },
  };
}

export interface PaintingMinimapFit {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly scale: number;
}

function imageOffset(viewport: number, image: number, focus: number, scale: number): number {
  if (image <= viewport) return (viewport - image) / 2;
  return Math.min(0, Math.max(viewport - image, viewport / 2 - focus * scale));
}

/** Contain the full painting, or cover a circular viewport, at zoom 1; larger zooms centre the focus without
 * showing empty strips. Convert a marker pixel with [x + pixel.x * scale, y + pixel.y * scale]. */
export function fitPaintingMinimap(
  viewport: Vec2,
  imageSize: Vec2,
  zoom: number,
  focusPx: Vec2 = [imageSize[0] / 2, imageSize[1] / 2],
  fit: 'contain' | 'cover' = 'contain'
): PaintingMinimapFit {
  if (
    ![...viewport, ...imageSize, zoom, ...focusPx].every(Number.isFinite) ||
    viewport.some((size) => size < 0) ||
    imageSize.some((size) => size <= 0) ||
    zoom < 1
  )
    throw new RangeError('Painting minimap needs finite dimensions and zoom >= 1');
  const scale =
    (fit === 'cover' ? Math.max : Math.min)(
      viewport[0] / imageSize[0],
      viewport[1] / imageSize[1]
    ) * zoom;
  const width = imageSize[0] * scale,
    height = imageSize[1] * scale;
  return {
    x: imageOffset(viewport[0], width, focusPx[0], scale),
    y: imageOffset(viewport[1], height, focusPx[1], scale),
    width,
    height,
    scale,
  };
}
